/* eslint-disable tree-shaking/no-side-effects-in-initialization -- webpack entry ponints are allowed to be side effectful */

/**
 * @file A very minimal entrypoint for embeds.
 * This allows embeds to initially load very quickly before then
 * coordinating with each other to load the heavyweight full application
 * in a staggered fashion using `embedStaggerWorker.ts`.
 */

import "./embedEntry.scss";
// eslint-disable-next-line no-restricted-imports -- this is a special case where we don't want to use logging.ts
import { datadogLogs } from "@datadog/browser-logs";
import { retry } from "@hex/common";

import type {
  EmbedStaggerReq,
  EmbedStaggerRes,
} from "./embedStaggerWorkerTypes.js";
import { startAuthSwapTokenExchange } from "./util/authSwapTokenExchange.js";
import { analyticsEnabled } from "./util/data.js";
import { setOnEmbedLoadedCallback } from "./util/embedLoaded.js";
import { initDatadogLogging } from "./util/initDatadog.js";

// copied from @hex/common so we don't need an extra import in this file
function guardNever(_value: never, safeVariant: unknown | undefined): Error {
  return new Error(`Unhandled discriminated union member: ${safeVariant}`);
}

if (analyticsEnabled) {
  try {
    initDatadogLogging();
  } catch (e) {
    console.error("Error initializing Datadog", e);
  }
}

// Started here rather than in `./index` because staggering can hold an embed
// back for the better part of a minute, and the swap token expires. `./index`
// awaits this same exchange before it renders.
void startAuthSwapTokenExchange();

let loadingStarted = false;
function loadApp(): void {
  if (loadingStarted) return;
  loadingStarted = true;

  void retry({
    func: () => import("./index"),
    retryCount: 5,
    interval: 150,
  });
}

// If the browser doesn't support shared workers, just load the app immediately
if (window.SharedWorker == null) {
  loadApp();
} else {
  // Otherwise, use embedStaggerWorker to coordinating loading across embeds
  try {
    const sendMsg = (msg: EmbedStaggerReq): void => {
      embedWorker.port.postMessage(msg);
    };

    const handleMsg = (e: MessageEvent): void => {
      const msg = e.data as EmbedStaggerRes;
      if (msg.type === "start_load_res") {
        startLoading();
      } else if (msg.type === "request_to_load_ack_res") {
        clearTimeout(noRespTimeout);
      } else {
        guardNever(msg, (msg as EmbedStaggerRes).type);
      }
    };

    const startLoading = (): void => {
      setOnEmbedLoadedCallback(() => {
        sendMsg({ type: "done_loading_req" });
        window.removeEventListener("beforeunload", handleUnload);
      });
      loadApp();
    };

    const handleUnload = (): void => {
      sendMsg({ type: "close_req" });
    };

    const embedWorker = new SharedWorker(
      new URL("./embedStaggerWorker", import.meta.url),
    );
    embedWorker.port.addEventListener("message", handleMsg);
    embedWorker.port.start();

    // For some reason, only in Chrome incognito windows,
    // when a Hex app is embedded in a non-Hex domain,
    // our messages to our SharedWorker just don't work.
    // No errors are thrown, they just silently disappear into the void.
    // This timeout ensures that if that does happen we load right away.
    // See https://linear.app/hex/issue/SUP-927/extremely-slow-load-time-for-embeds-in-incognito
    const noRespTimeoutMs = 2_000;
    const noRespTimeout = window.setTimeout(() => {
      if (loadingStarted) return;

      loadApp();

      const logMsg = "Shared worker did not respond, loading immediately";
      if (analyticsEnabled) {
        datadogLogs.logger.error(logMsg);
      } else {
        console.error(logMsg);
      }
    }, noRespTimeoutMs);

    sendMsg({ type: "request_to_load_req" });

    // This might not always work, but try to let the worker know if the tab is closed before loading ends
    window.addEventListener("beforeunload", handleUnload);

    // Set a backup plan just in case the SharedWorker has some issue:
    // if the embed hasn't loaded in 30s, load it sometime in the next 30s
    const forceLoadTimeoutMs = 30_000 + Math.random() * 30_000;
    window.setTimeout(() => {
      if (loadingStarted) return;

      loadApp();

      const logMsg = "Embed loading not triggered until timeout";
      if (analyticsEnabled) {
        datadogLogs.logger.error(logMsg);
      } else {
        console.error(logMsg);
      }
    }, forceLoadTimeoutMs);
  } catch (e: unknown) {
    const err = e instanceof Error ? e : new Error(JSON.stringify(e));
    const logMsg = "Error using SharedWorker to load embed";
    if (analyticsEnabled) {
      datadogLogs.logger.error(logMsg, undefined, err);
    } else {
      console.error(logMsg, err);
    }

    // After logging the error, just try to load the app directly,
    // better to at least attempt to load it then do nothing.
    loadApp();
  }
}
